import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { CheckCircleOutline, LocationOnOutlined, PauseCircleOutline, PlaceOutlined } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import {
  checkEditorPermission,
  checkViewerPermission,
  getAdministrationRedirectPath,
  isProvisioningLoaded,
} from "services/utils/HelperFunction";
import { StatCardGrid, StatutoryList, StatutoryPage } from "pages/PayRoll/Shared/statutoryUi";
import { NewRequestButton } from "pages/TeamZone/Approve/approveUi";
import { fromApiSiteRow, percentOf, siteHeaders } from "./sitesData";
import { exportSites, getSite, getSiteSummary, getSites, saveBlob } from "./sitesApi";
import { AddEditSiteDrawer, SiteDetailsDrawer } from "./SitePanels";
import { SiteActionModals } from "./SiteModals";

// Administration > Sites: where employees work. A site can host several
// companies (from the employees working there). Search, the status cards,
// sorting and paging run on the server.

const ALL = "all";
const SEARCH_DELAY = 400;
const PAGE_SIZE = 25;
const EXPORT_PAGE_SIZE = 100;
const MODULE = "Sites";
const SUB_MODULE = "Site";
const ACTIVE_COLORS = { Active: "#12B76A", Inactive: "#667085" };

export const CompanyChips = ({ companies }) => {
  if (!companies?.length) return "—";
  const shown = companies.slice(0, 2);
  const rest = companies.slice(2);
  const chipSx = { height: 24, fontSize: 12, borderRadius: "6px", backgroundColor: designColors.slate100, color: designColors.slate700 };
  return (
    <Mui.Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
      {shown.map((company) => (
        <Mui.Chip key={company.id} size="small" label={company.name} sx={chipSx} />
      ))}
      {rest.length ? (
        <Mui.Tooltip title={rest.map((company) => `${company.name} (${company.employee_count})`).join(", ")} arrow>
          <Mui.Chip size="small" label={`+${rest.length}`} sx={{ ...chipSx, fontWeight: 600 }} />
        </Mui.Tooltip>
      ) : null}
    </Mui.Stack>
  );
};

export const Sites = () => {
  const { domain, userScreenAccess } = useSelector((state) => state.tokenReducer);
  const history = useHistory();
  const modules = userScreenAccess?.module_details;
  const hasViewAccess = checkViewerPermission(modules, MODULE, SUB_MODULE);
  const canManage = checkEditorPermission(modules, MODULE, SUB_MODULE);
  const provisioningLoaded = isProvisioningLoaded(userScreenAccess, "Administration");

  // Same gate as Companies: no viewer access -> another screen they can see.
  useEffect(() => {
    if (!provisioningLoaded || hasViewAccess) return;
    history.replace(getAdministrationRedirectPath(modules));
  }, [provisioningLoaded, hasViewAccess, modules, history]);

  if (!hasViewAccess) return null;
  return <SitesScreen key={domain} domain={domain} canManage={canManage} />;
};

const SitesScreen = ({ domain, canManage }) => {
  const { userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [card, setCard] = useState(ALL);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [reloadKey, setReloadKey] = useState(0);
  const [list, setList] = useState({ loading: true, error: "", rows: [], count: 0 });
  const [summary, setSummary] = useState({ total: 0, active: 0, inactive: 0 });
  const [viewId, setViewId] = useState(null);
  const [details, setDetails] = useState({ loading: false, error: "", site: null });
  const [detailsKey, setDetailsKey] = useState(0);
  const [form, setForm] = useState(null); // { mode: "add" } | { mode: "edit", site }
  const [action, setAction] = useState(null); // { type, site }
  const firstSearch = useRef(true);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "administration_sites_saved_views",
    fullHeaders: siteHeaders,
  });

  useEffect(() => {
    if (firstSearch.current) {
      firstSearch.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [search]);

  const listFilters = {
    search: query,
    status: card === ALL ? "" : card,
    ordering: "name",
  };
  const filterKey = JSON.stringify(listFilters);

  useEffect(() => {
    const controller = new AbortController();
    setList((current) => ({ ...current, loading: true, error: "" }));
    getSites({ domain, ...JSON.parse(filterKey), page: page + 1, pageSize, signal: controller.signal })
      .then((data) => setList({ loading: false, error: "", rows: (data.results || []).map(fromApiSiteRow), count: data.count || 0 }))
      .catch((error) => {
        if (controller.signal.aborted) return;
        setList((current) => ({ ...current, loading: false, error: error.message || "Could not load sites." }));
      });
    return () => controller.abort();
  }, [domain, filterKey, page, pageSize, reloadKey]);

  useEffect(() => {
    const controller = new AbortController();
    getSiteSummary({ domain, signal: controller.signal })
      .then(setSummary)
      .catch(() => null);
    return () => controller.abort();
  }, [domain, reloadKey]);

  useEffect(() => {
    if (viewId == null) {
      setDetails({ loading: false, error: "", site: null });
      return undefined;
    }
    const controller = new AbortController();
    setDetails((current) => ({ loading: true, error: "", site: current.site?.id === viewId ? current.site : null }));
    getSite({ domain, id: viewId, signal: controller.signal })
      .then((site) => setDetails({ loading: false, error: "", site }))
      .catch((error) => {
        if (controller.signal.aborted) return;
        setDetails({ loading: false, error: error.message || "Could not load the site.", site: null });
      });
    return () => controller.abort();
  }, [domain, viewId, detailsKey]);

  const reloadList = () => setReloadKey((key) => key + 1);
  const refreshAll = () => {
    reloadList();
    setDetailsKey((key) => key + 1);
  };

  const changeCard = (key) => {
    setCard(key);
    setPage(0);
  };

  const afterSave = (site, message) => {
    const wasEdit = form?.mode === "edit";
    setForm(null);
    showToast("success", message || "Site saved");
    reloadList();
    if (wasEdit) {
      setDetails({ loading: false, error: "", site });
      setViewId(site.id);
    }
  };

  const exportRows = useCallback(async () => {
    const all = [];
    for (let next = 1; ; next += 1) {
      // eslint-disable-next-line no-await-in-loop
      const data = await getSites({ domain, ...JSON.parse(filterKey), page: next, pageSize: EXPORT_PAGE_SIZE });
      all.push(...(data.results || []).map(fromApiSiteRow));
      if (!data.results?.length || all.length >= data.count) return all;
    }
  }, [domain, filterKey]);

  // Excel comes from the server export (same filters); PDF / CSV are built
  // from every matching row.
  const downloadFile = async (format) => {
    if (format !== "excel") return false;
    try {
      saveBlob(await exportSites({ domain, ...JSON.parse(filterKey) }), "sites.xlsx");
    } catch (error) {
      showToast("error", error.message || "Could not download the sites.");
    }
    return true;
  };

  const cards = [
    { key: ALL, title: "Total sites", value: summary.total, detail: "100% of all sites", icon: PlaceOutlined, tone: "blue" },
    { key: "active", title: "Active", value: summary.active, detail: `${percentOf(summary.active, summary.total)}% of all sites`, icon: CheckCircleOutline, tone: "green" },
    { key: "inactive", title: "Inactive", value: summary.inactive, detail: `${percentOf(summary.inactive, summary.total)}% of all sites`, icon: PauseCircleOutline, tone: "slate" },
  ];

  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return (
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <LocationOnOutlined sx={{ fontSize: 18, color: designColors.tealDark }} />
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
          </Mui.Stack>
        );
      case "status_label":
        return <RequestStatusChip status={row.status_label} colorCode={ACTIVE_COLORS[row.status_label]} />;
      case "companies_label":
        return <CompanyChips companies={row.companies} />;
      case "employee_count":
        return Number(row.employee_count).toLocaleString("en-IN");
      default:
        return row[key] || "—";
    }
  };

  // Same header button as the Team Workspace Approve screens' "New Request".
  const addButton = (
    <NewRequestButton hasAccess={canManage} onClick={() => setForm({ mode: "add" })}>
      Add Site
    </NewRequestButton>
  );

  return (
    <StatutoryPage
      breadcrumb={["Administration", "Sites"]}
      title="Sites"
      subtitle="Places where your employees work. A site decides Professional Tax and LWF, and can host several companies."
      aside={addButton}
    >
      {list.error ? (
        <Mui.Box sx={{ mb: 1.5 }}>
          <RequestInfoBanner
            tone="danger"
            title="Couldn't load the sites."
            body={list.error}
            action={<SecondaryButton onClick={reloadList} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
          />
        </Mui.Box>
      ) : null}
      <StatCardGrid cards={cards} value={card} onChange={changeCard} />
      <StatutoryList
        title="Sites"
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={list.rows}
        loading={list.loading}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel="sites"
        searchPlaceholder="Search name, code or city"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
        server={{
          search,
          onSearchChange: setSearch,
          page,
          rowsPerPage: pageSize,
          count: list.count,
          onPageChange: setPage,
          onRowsPerPageChange: (size) => {
            setPageSize(size);
            setPage(0);
          },
          exportRows,
          downloadFile,
        }}
      />

      <SiteDetailsDrawer
        open={viewId != null && !form}
        loading={details.loading && !details.site}
        error={details.error}
        site={details.site}
        canManage={canManage}
        onRetry={() => setDetailsKey((key) => key + 1)}
        onClose={() => setViewId(null)}
        onEdit={(site) => setForm({ mode: "edit", site })}
        onAction={(type, site) => setAction({ type, site })}
      />
      <AddEditSiteDrawer
        open={Boolean(form)}
        mode={form?.mode}
        site={form?.site}
        domain={domain}
        onClose={() => setForm(null)}
        onSaved={afterSave}
      />
      <SiteActionModals
        action={action}
        domain={domain}
        onChange={setAction}
        onClose={() => setAction(null)}
        onDone={({ message, deleted }) => {
          setAction(null);
          if (message) showToast("success", message);
          if (deleted) setViewId(null);
          refreshAll();
        }}
      />
    </StatutoryPage>
  );
};

export default Sites;
